/**
 * One calendar for the patient (2.5): the certification period as week rows and
 * day columns, with every discipline's visits on it — real ones (delivered with
 * the check, scheduled, missed) and planned placeholders hatched, as the week
 * view draws them — coloured by discipline, the clinician's initials on each
 * chip, supervisory and recert visits marked as the track table marks them.
 * Clicking a chip opens Why this clinician.
 *
 * It reads the same plan visits, bindings and auth coverage the track table and
 * the week view read. Nothing new is computed here; the plan is drawn once, on
 * one grid, instead of once per discipline.
 */
import { useMemo, useState } from "react";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { selectAuthCoverage, type AuthCoverage } from "../../engine/authLedger";
import { addDaysToKey, dateKeyStart, formatInBranchZone, selectDemoNow, toDateKey, weekStartKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { disciplineGroupFor, visitTypeLabel } from "../../engine/roster";
import { reservedLabel } from "../../engine/reserved";
import { DONE, RESERVED, SCHEDULED } from "./visitStatusColors";
import type { DisciplineGroup, Episode, PlanVisit } from "@caredna-marlin/utils/homeHealthDemo/state";
import { useDemoState } from "../../store/useDemoStore";
import { WhyThisClinician } from "./WhyThisClinician";
import { useBranchClock } from "../../components/branchClock";

/**
 * **Status carries the chip, not discipline** (Pete, 2026-09-21), and it carries
 * it the way Who is working carries a shift: a day already behind you is the
 * grey of a worked day, a scheduled visit still to come is the shift blue, and a
 * reservation is that blue thinned, exactly as an overflow shift is. The chip
 * now reads the **visit type** rather than the clinician's discipline, so the
 * discipline is on the chip in words and does not need a hue as well.
 */

/**
   * **There is no missed.** Pete, through the script author on 2026-09-22: the
   * product cancels a visit with a reason, and that is the only status short of
   * delivered. *Missed* is HCHB's word and lives in the 4.2 talk track. A plan
   * visit the store marks `missed` is one whose real visit was canceled, so the
   * calendar hides it exactly as it hides a canceled one and the worklist's
   * **Canceled, rebook** row carries the event.
   */

/** Still used by the plan's own track table, which is a per-discipline read and does want a hue. */
export const GROUP_COLORS: Record<DisciplineGroup, string> = {
  SN: "#3f6fb0",
  PT: "#2e7d5b",
  OT: "#7a53a8",
  SLP: "#b0702e",
  MSW: "#55707f",
  HHA: "#a03b63",
};

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function EpisodeCalendar({ episode, visits }: { episode: Episode; visits: PlanVisit[] }) {
  const state = useDemoState();
  const tz = state.config.branch.timeZone;
  const clock = useBranchClock(tz);
  const today = toDateKey(selectDemoNow(state, new Date()), tz);
  const [whyId, setWhyId] = useState<string | undefined>(undefined);
  const gated = state.patients[episode.patientRef]?.payer === "MA";
  const coverage = useMemo(() => selectAuthCoverage(episode, visits, gated), [episode, visits, gated]);

  const weeks = useMemo(() => {
    const first = weekStartKey(episode.socDate);
    const last = weekStartKey(episode.certEndDate);
    const out: string[] = [];
    for (let w = first; w <= last; w = addDaysToKey(w, 7)) out.push(w);
    return out;
  }, [episode.socDate, episode.certEndDate]);

  const byDay = useMemo(() => {
    const map = new Map<string, PlanVisit[]>();
    for (const v of visits) {
      if (v.status === "canceled" || v.status === "missed") continue;
      map.set(v.targetDate, [...(map.get(v.targetDate) ?? []), v]);
    }
    for (const list of map.values()) list.sort((a, b) => (a.windowStart ?? "").localeCompare(b.windowStart ?? "") || a.discipline.localeCompare(b.discipline));
    return map;
  }, [visits]);

  const groups = useMemo(() => Array.from(new Set(visits.map((v) => disciplineGroupFor(v.discipline)))), [visits]);
  const fmt = (d: string, p = "MMM d") => formatInBranchZone(dateKeyStart(d, tz), tz, p);

  return (
    <Box data-episode-calendar={episode.id}>
      {/* No heading and no date range: the dialog is already titled Calendar and
          every week row carries its own dates (Pete, 2026-09-21). */}
      <Box sx={{ overflowX: "auto" }}>
        <Box sx={{ minWidth: 720 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "84px repeat(7, minmax(0, 1fr))", gap: 0.5, mb: 0.5 }}>
            <Box />
            {DAY_LABELS.map((d) => (
              <Typography key={d} sx={{ fontSize: 11, fontWeight: 600, color: "text.secondary" }}>
                {d}
              </Typography>
            ))}
          </Box>
          {weeks.map((weekStart) => {
            const isNow = today >= weekStart && today <= addDaysToKey(weekStart, 6);
            return (
              <Box key={weekStart} data-calendar-week={weekStart} sx={{ display: "grid", gridTemplateColumns: "84px repeat(7, minmax(0, 1fr))", gap: 0.5, mb: 0.5 }}>
                {/* Calendar weeks run Monday to Sunday; the track table's "Wk N" are SOC-anchored and start on
                    the day of the week the start of care fell on. Two numberings on one page would read as one,
                    so the rows here carry their dates and no number. */}
                <Box sx={{ pt: 0.5 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: isNow ? 700 : 600 }}>{fmt(weekStart)}</Typography>
                  <Typography sx={{ fontSize: 10, color: "text.secondary", lineHeight: 1.1 }}>
                    – {fmt(addDaysToKey(weekStart, 6))}
                  </Typography>
                </Box>
                {Array.from({ length: 7 }, (_, i) => addDaysToKey(weekStart, i)).map((day) => {
                  const inEpisode = day >= episode.socDate && day <= episode.certEndDate;
                  const dayVisits = byDay.get(day) ?? [];
                  return (
                    <Box
                      key={day}
                      data-calendar-day={day}
                      sx={{
                        border: "1px solid #e6e6e6",
                        borderRadius: 0.5,
                        minHeight: 44,
                        p: 0.375,
                        bgcolor: !inEpisode ? "#fafafa" : day === today ? "#fff4d6" : isNow ? "#fffaf0" : "#fff",
                        opacity: inEpisode ? 1 : 0.5,
                      }}
                    >
                      <Typography sx={{ fontSize: 9.5, color: "text.secondary", lineHeight: 1 }}>{fmt(day, "d")}</Typography>
                      <Stack spacing={0.25} sx={{ mt: 0.25 }}>
                        {dayVisits.map((v) => (
                          <VisitChip key={v.id} visit={v} coverage={coverage.byVisitId[v.id]} onClick={() => setWhyId(v.id)} />
                        ))}
                      </Stack>
                    </Box>
                  );
                })}
              </Box>
            );
          })}
        </Box>
      </Box>

      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 1, fontSize: 11.5, color: "text.secondary" }}>
        <Swatch label="done" sx={DONE} />
        <Swatch label="scheduled" sx={SCHEDULED} />
        <Swatch label="reserved" sx={RESERVED} />
      </Stack>

      {whyId && <WhyThisClinician planVisitId={whyId} onClose={() => setWhyId(undefined)} />}
    </Box>
  );
}

/** "Dana Reyes" → "DR"; the chip carries the clinician, never the discipline twice. */
export function initialsOf(displayName: string | undefined): string {
  if (!displayName) return "—";
  const parts = displayName.split(/\s+/).filter(Boolean);
  return parts.slice(0, 2).map((p) => p[0]?.toUpperCase() ?? "").join("");
}

export function glyphFor(visit: PlanVisit): string {
  if (visit.rule === "supervisory") return "S";
  if (visit.rule === "recert") return "R";
  if (visit.visitTypeKind === "SOC") return "SOC";
  if (visit.visitTypeKind === "RECERT") return "R";
  if (visit.visitTypeKind.endsWith("_EVAL")) return "E";
  return "";
}

function VisitChip({ visit, coverage, onClick }: { visit: PlanVisit; coverage?: AuthCoverage; onClick: () => void }) {
  const state = useDemoState();
  const tz = state.config.branch.timeZone;
  const clock = useBranchClock(tz);
  const clinician = visit.providerHandle ? state.roster[visit.providerHandle]?.displayName : undefined;
  const today = toDateKey(selectDemoNow(state, new Date()), tz);
  const past = visit.status === "completed" || visit.targetDate < today;
  const style: Record<string, unknown> = past ? { ...DONE } : visit.status === "real" ? { ...SCHEDULED } : { ...RESERVED };

  // A reservation says the one word with its reason, as My week does.
  const status =
    visit.status === "completed"
      ? "delivered"
      : visit.status === "real"
        ? "scheduled"
        : reservedLabel(coverage === "beyondAuth" ? "pendingAuth" : visit.rule === "recert" ? "recertWindow" : "planned");

  return (
    <Tooltip
      title={`${visitTypeLabel(state.config, visit.visitTypeKind)} · ${formatInBranchZone(dateKeyStart(visit.targetDate, tz), tz, "EEE MMM d")}${visit.windowStart ? ` · ${clock.wall(visit.windowStart)}` : ""} · ${clinician ?? "unassigned"} · ${status}${visit.rule ? ` · rule: ${visit.rule}` : ""}`}
    >
      <Box
        component="span"
        onClick={onClick}
        data-plan-visit={visit.id}
        data-calendar-chip={visit.id}
        data-group={disciplineGroupFor(visit.discipline)}
        data-status={visit.status}
        data-coverage={coverage ?? ""}
        sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.25, px: 0.375, borderRadius: 0.5, fontSize: 9.5, lineHeight: 1.5, whiteSpace: "nowrap", overflow: "hidden", ...style }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{visitTypeLabel(state.config, visit.visitTypeKind)}</span>
        <span style={{ marginLeft: "auto", opacity: 0.8, paddingLeft: 3 }}>{initialsOf(clinician)}</span>
      </Box>
    </Tooltip>
  );
}

function Swatch({ label, sx }: { label: string; sx: Record<string, unknown> }) {
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
      <Box component="i" sx={{ display: "inline-block", width: 14, height: 12, border: "1px solid #ccc", ...sx }} />
      {label}
    </Box>
  );
}
